import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import type { PreOrder } from '../../../types/catalog';
import CheckoutDialog from './CheckoutDialog';
import { useCatalogCart } from './useCatalogCart';
import { useCatalogViewData } from './useCatalogViewData';
import { useSavedPhone } from './useSavedPhone';

const formatPrice = (value: number): string => `$${value.toFixed(2)}`;

export default function CatalogViewPage() {
  const { catalogId } = useParams<{ catalogId: string }>();
  const navigate = useNavigate();
  const parsedCatalogId = parseInt(catalogId ?? '');

  const [placedOrder, setPlacedOrder] = useState<PreOrder | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const { data: view, pagination, filters } = useCatalogViewData({ catalogId: parsedCatalogId });
  const { data: cart, state: cartState, actions: cartActions } = useCatalogCart({ catalogId: parsedCatalogId });

  const {
    data: { savedPhone },
    actions: { savePhone },
  } = useSavedPhone();

  const { catalog } = view;

  if (view.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (view.notFound || !catalog) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color={view.error ? 'error' : 'text.primary'}>
          {view.error ?? 'Каталог не знайдено або у вас немає доступу'}
        </Typography>
      </Box>
    );
  }

  if (placedOrder) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 800, mx: 'auto', width: '100%' }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Дякуємо! Замовлення №{placedOrder.id} прийнято — з вами зв'яжуться для підтвердження.
        </Alert>
        <List>
          {placedOrder.items.map((item) => (
            <ListItem key={item.productId} divider>
              <ListItemText
                primary={item.productName}
                secondary={`${item.quantity} × ${formatPrice(item.unitPrice)}`}
              />
              <Typography variant="body1">{formatPrice(item.quantity * item.unitPrice)}</Typography>
            </ListItem>
          ))}
        </List>
        <Typography variant="h6" sx={{ textAlign: 'right', mt: 1, mb: 3 }}>
          Разом: {formatPrice(placedOrder.total)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            color="inherit"
            onClick={() => setPlacedOrder(null)}
          >
            Повернутися до каталогу
          </Button>
          <Button variant="text" onClick={() => navigate('/my-preorders')}>
            Мої передзамовлення
          </Button>
        </Box>
      </Box>
    );
  }

  const hasItems = Object.keys(cart.quantities).length > 0;

  const handleOpenCheckout = () => {
    setCheckoutOpen(true);
  };

  const handleCloseCheckout = () => {
    setCheckoutOpen(false);
  };

  const handleCheckout = async (phone: string) => {
    const preOrder = await cartActions.checkout(phone);
    if (preOrder) {
      savePhone(phone);
      setCheckoutOpen(false);
      setPlacedOrder(preOrder);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 800, mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="h4" component="h1" noWrap sx={{ minWidth: 0 }}>
          {catalog.name}
        </Typography>
        <Tooltip title="Показати/сховати пошук">
          <IconButton
            size="small"
            onClick={() => setShowSearch(!showSearch)}
            color={showSearch || filters.search ? 'primary' : 'default'}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {view.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {view.error}
        </Alert>
      )}

      {cart.error && !checkoutOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cart.error}
        </Alert>
      )}

      {cart.unavailableItems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Ці товари з вашого кошика більше недоступні:{' '}
          {cart.unavailableItems.map((item) => item.productName ?? item.productId).join(', ')}.
          Вони не потраплять у замовлення.
        </Alert>
      )}

      {/* Search */}
      <Collapse in={showSearch}>
        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'background.default' }} variant="outlined">
          <TextField
            label="Пошук за назвою або кодом"
            variant="outlined"
            size="small"
            fullWidth
            value={filters.search}
            onChange={(e) => filters.setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={filters.handleClearSearch}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Paper>
      </Collapse>
      <Box sx={{ height: 4, mb: 1 }}>
        {view.fetching && <LinearProgress />}
      </Box>

      {/* Content */}
      {catalog.items.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          {filters.debouncedSearch ? 'За вашим запитом нічого не знайдено' : 'У каталозі поки немає товарів'}
        </Typography>
      ) : (
        <List>
          {catalog.items.map((item) => {
            const quantity = cart.quantities[item.productId] ?? 0;
            return (
              <ListItem key={item.productId} divider>
                <ListItemText
                  primary={item.productName}
                  secondary={item.productCode}
                />
                <Typography variant="body1" sx={{ fontWeight: 500, mr: 2 }}>
                  {formatPrice(item.unitPrice)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    aria-label="Зменшити кількість"
                    disabled={quantity === 0}
                    onClick={() => cartActions.setQuantity(item, quantity - 1)}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ minWidth: 28, textAlign: 'center' }}>{quantity}</Typography>
                  <IconButton
                    size="small"
                    aria-label="Збільшити кількість"
                    disabled={quantity >= 9999}
                    onClick={() => cartActions.setQuantity(item, quantity + 1)}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Pagination */}
      {pagination.count > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            {...pagination}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Footer */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          backgroundColor: 'background.default',
        }}
      >
        <Typography variant="h6">Разом: {formatPrice(cart.total)}</Typography>
        <Button
          variant="contained"
          disabled={!hasItems || cartState.checkingOut}
          onClick={handleOpenCheckout}
        >
          Оформити замовлення
        </Button>
      </Box>

      <CheckoutDialog
        open={checkoutOpen}
        lines={cart.lines}
        total={cart.total}
        checkingOut={cartState.checkingOut}
        error={cart.error}
        initialPhone={savedPhone}
        onClose={handleCloseCheckout}
        onConfirm={handleCheckout}
      />
    </Box>
  );
}
